/**
 * Reiter *Stammdaten* für ein Grundstück (Spec 01, „Oberfläche", erweitert
 * um Spec 02a, „Reiter Stammdaten": Archivieren geht seit 02a über
 * `archive-preview`, siehe `ArchiveControls`).
 *
 * Anders als das Sheet: alle acht Felder direkt sichtbar, kein „Weitere
 * Angaben" — der Datensatz existiert bereits, es gibt nichts mehr zu
 * verbergen.
 *
 * Die Lesedaten (`query`) kommen als Prop von `routes/ObjectDetail.tsx`
 * (Container pro Bildschirm besitzt die Query); diese Komponente besitzt nur
 * den lokalen Bearbeitungszustand und die Schreib-Mutationen.
 * `canUnarchive` kommt ebenfalls von dort — es steht nur auf `ObjectHead`
 * (`GET /entities/{id}`), nicht auf `PropertyDetail` (I9: der Client
 * berechnet nichts selbst).
 */

import type { UseQueryResult } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ArchiveControls } from '@/components/ArchiveControls'
import { ArchivedBanner } from '@/components/ArchivedBanner'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { centsToDecimalInput, parseCents } from '@/lib/money'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import type { PropertyDetail } from '@/features/properties/api'
import {
  useArchivePreview,
  useArchiveProperty,
  useUnarchiveProperty,
  useUpdateProperty,
} from '@/features/properties/api'
import { FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface PropertyMasterDataProps {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  /** `ObjectHead.archived_by_id`/`archived_by_label` (Spec 02b) — kommen von
   * `routes/ObjectDetail.tsx`, nicht von `PropertyDetail`: Sie stehen nur auf
   * `ObjectHead` (I9). */
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly query: UseQueryResult<PropertyDetail>
}

interface FormValues {
  readonly street: string
  readonly postalCode: string
  readonly city: string
  readonly areaSqm: string
  readonly landRegistryRef: string
  readonly parcelNumber: string
  readonly acquiredOn: string
  readonly acquisitionCost: string
}

function deriveForm(property: PropertyDetail): FormValues {
  return {
    street: property.street ?? '',
    postalCode: property.postal_code ?? '',
    city: property.city ?? '',
    areaSqm: property.area_sqm === null ? '' : areaToInputValue(property.area_sqm),
    landRegistryRef: property.land_registry_ref ?? '',
    parcelNumber: property.parcel_number ?? '',
    acquiredOn: property.acquired_on ?? '',
    acquisitionCost:
      property.acquisition_cost_cents === null ? '' : centsToDecimalInput(property.acquisition_cost_cents),
  }
}

function PropertyReadField({ label, value, testId }: { readonly label: string; readonly value: string | null; readonly testId: string }) {
  return (
    <div>
      <dt className="text-label text-muted">{label}</dt>
      <dd data-testid={testId} className="text-body text-text">{value ?? '—'}</dd>
    </div>
  )
}

export function PropertyMasterData({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  query,
}: PropertyMasterDataProps) {
  return (
    <section data-testid="property-master-data" className="px-(--page-x) py-4">
      {query.isPending && <SkeletonRows testId="property-master-data-loading" rows={8} />}

      {query.isError && (
        <ErrorState
          testId="property-master-data-error"
          retryTestId="property-master-data-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && (
        <PropertyMasterDataForm
          entityId={entityId}
          canEdit={canEdit}
          canUnarchive={canUnarchive}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          property={query.data}
        />
      )}
    </section>
  )
}

function PropertyMasterDataForm({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  property,
}: {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly property: PropertyDetail
}) {
  const updateMutation = useUpdateProperty()
  const archiveMutation = useArchiveProperty()
  const unarchiveMutation = useUnarchiveProperty()
  const archivePreviewMutation = useArchivePreview()

  const [form, setForm] = useState<FormValues>(() => deriveForm(property))
  const [initial, setInitial] = useState<FormValues>(() => deriveForm(property))
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  // Neu einlesen, wenn sich der Datensatz selbst ändert — anderes Grundstück
  // geöffnet oder `version` nach einem eigenen Speichern hochgezählt. Ein
  // Hintergrund-Refetch mit unveränderter `version` überschreibt dagegen
  // nichts, was gerade bearbeitet wird. Während des Renderns verglichen
  // (React-Muster „Adjusting state when a prop changes"), nicht in einem
  // Effekt — sonst gäbe es einen zusätzlichen Durchlauf mit dem alten Stand.
  const [syncedKey, setSyncedKey] = useState(`${property.id}:${String(property.version)}`)
  const currentKey = `${property.id}:${String(property.version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    const next = deriveForm(property)
    setForm(next)
    setInitial(next)
  }

  const isArchived = property.archived_at !== null
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial)

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function openEditor() {
    const next = deriveForm(property)
    setForm(next)
    setInitial(next)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await updateMutation.mutateAsync({
        propertyId: entityId,
        ifMatch: String(property.version),
        body: {
          street: form.street,
          postal_code: form.postalCode.trim() === '' ? null : form.postalCode.trim(),
          city: form.city.trim() === '' ? null : form.city.trim(),
          area_sqm: parseAreaInput(form.areaSqm),
          land_registry_ref: form.landRegistryRef.trim() === '' ? null : form.landRegistryRef.trim(),
          parcel_number: form.parcelNumber.trim() === '' ? null : form.parcelNumber.trim(),
          acquired_on: form.acquiredOn === '' ? null : form.acquiredOn,
          acquisition_cost_cents: parseCents(form.acquisitionCost),
        },
      })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleUnarchive() {
    setArchiveError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ propertyId: entityId, ifMatch: String(property.version) })
    } catch (error) {
      setArchiveError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <dl data-testid="property-master-data-read" className="grid gap-3 sm:grid-cols-2">
          <PropertyReadField label="Straße und Hausnummer" value={property.street} testId="property-read-street" />
          <PropertyReadField label="PLZ" value={property.postal_code} testId="property-read-postal-code" />
          <PropertyReadField label="Ort" value={property.city} testId="property-read-city" />
          <PropertyReadField label="Fläche" value={property.area_sqm_display} testId="property-read-area-sqm" />
          <PropertyReadField label="Grundbuchblatt" value={property.land_registry_ref} testId="property-read-land-registry-ref" />
          <PropertyReadField label="Flurstück" value={property.parcel_number} testId="property-read-parcel-number" />
          <PropertyReadField label="Anschaffungsdatum" value={property.acquired_on} testId="property-read-acquired-on" />
          <PropertyReadField label="Anschaffungskosten" value={property.acquisition_cost_display} testId="property-read-acquisition-cost" />
        </dl>
        {canEdit && !isArchived && (
          <button type="button" ref={editTriggerRef} data-testid="property-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      {isArchived ? (
        <ArchivedBanner
          kind="property"
          canUnarchive={canUnarchive}
          parentId={null}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          onUnarchive={() => void handleUnarchive()}
          isUnarchiving={unarchiveMutation.isPending}
        />
      ) : (
        <ArchiveControls
          kind="property"
          entityId={entityId}
          label={property.label}
          version={property.version}
          parentId={null}
          isArchiving={archiveMutation.isPending}
          onArchive={(ifMatch) => archiveMutation.mutateAsync({ propertyId: entityId, ifMatch })}
          onError={setArchiveError}
          archivePreview={archivePreviewMutation}
        />
      )}
      {archiveError !== undefined && <p data-testid="property-archive-error" role="alert" className="text-label text-status-overdue">{archiveError}</p>}
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Grundstück bearbeiten"
        triggerRef={editTriggerRef}
        formId="property-edit-form"
        saveTestId="property-edit-save"
        saveDisabled={!isDirty || updateMutation.isPending}
        isSaving={updateMutation.isPending}
      >
    <form id="property-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
      <FormField
        label="Straße und Hausnummer"
        testId="property-edit-street"
        apiField="street"
        errorPrefix="property-edit"
        required
        disabled={isArchived}
        value={form.street}
        onChange={updateField('street')}
        error={fieldError}
      />
      <FormField
        label="PLZ"
        testId="property-edit-postal-code"
        apiField="postal_code"
        errorPrefix="property-edit"
        inputMode="numeric"
        disabled={isArchived}
        value={form.postalCode}
        onChange={updateField('postalCode')}
        error={fieldError}
      />
      <FormField
        label="Ort"
        testId="property-edit-city"
        apiField="city"
        errorPrefix="property-edit"
        disabled={isArchived}
        value={form.city}
        onChange={updateField('city')}
        error={fieldError}
      />
      <FormField
        label="Fläche (m²)"
        testId="property-edit-area-sqm"
        apiField="area_sqm"
        errorPrefix="property-edit"
        inputMode="decimal"
        disabled={isArchived}
        value={form.areaSqm}
        onChange={updateField('areaSqm')}
        error={fieldError}
      />
      <FormField
        label="Grundbuchblatt"
        testId="property-edit-land-registry-ref"
        apiField="land_registry_ref"
        errorPrefix="property-edit"
        disabled={isArchived}
        value={form.landRegistryRef}
        onChange={updateField('landRegistryRef')}
        error={fieldError}
      />
      <FormField
        label="Flurstück"
        testId="property-edit-parcel-number"
        apiField="parcel_number"
        errorPrefix="property-edit"
        disabled={isArchived}
        value={form.parcelNumber}
        onChange={updateField('parcelNumber')}
        error={fieldError}
      />
      <FormField
        label="Anschaffungsdatum"
        testId="property-edit-acquired-on"
        apiField="acquired_on"
        errorPrefix="property-edit"
        type="date"
        disabled={isArchived}
        value={form.acquiredOn}
        onChange={updateField('acquiredOn')}
        error={fieldError}
      />
      <FormField
        label="Anschaffungskosten"
        testId="property-edit-acquisition-cost"
        apiField="acquisition_cost_cents"
        errorPrefix="property-edit"
        inputMode="decimal"
        disabled={isArchived}
        value={form.acquisitionCost}
        onChange={updateField('acquisitionCost')}
        error={fieldError}
      />

      {generalError !== undefined && (
        <p data-testid="property-edit-error" role="alert" className="text-label text-status-overdue">
          {generalError}
        </p>
      )}

    </form>
      </MasterDataEditSheet>
      <ConfirmationStrip
        message={confirmation}
        onDismiss={() => {
          setConfirmation(null)
        }}
        testId="object-save-confirmation"
      />
    </>
  )
}
